"use client";

import { useState } from "react";

type ContactSection = {
  title: string;
  description: string;
  email: string;
};

const contactSections: ContactSection[] = [
  {
    title: "Customer Contact",
    description:
      "Questions about AGPSIRA, Share-In-Revenues (SiR), agreements, participant onboarding, distributions, account assistance, and general platform inquiries.",
    email: "info@agpsira.com",
  },

  {
    title: "Business Contact",
    description:
      "Partnership opportunities, Business Service Provider (BSP) participation, External Account Holder (EAH) relationships, enterprise solutions, strategic alliances, and business development.",
    email: "biz@agpsira.com",
  },
];

export default function ContactUsPage() {
  const [name, setName] =
    useState("");

  const [email, setEmail] =
    useState("");

  const [company, setCompany] =
    useState("");

  const [subject, setSubject] =
    useState("");

  const [message, setMessage] =
    useState("");

  const [errors, setErrors] =
    useState<string[]>([]);

  const [successMessage, setSuccessMessage] =
    useState("");

  function validateForm(): boolean {
    const validationErrors: string[] = [];

    if (!name.trim()) {
      validationErrors.push(
        "Name is required."
      );
    }

    if (!email.trim()) {
      validationErrors.push(
        "Email address is required."
      );
    }

    const emailRegex =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (
      email.trim() &&
      !emailRegex.test(email)
    ) {
      validationErrors.push(
        "Please enter a valid email address."
      );
    }

    if (!subject.trim()) {
      validationErrors.push(
        "Subject is required."
      );
    }

    if (!message.trim()) {
      validationErrors.push(
        "Message is required."
      );
    }

    if (
      message.trim().length > 0 &&
      message.trim().length < 20
    ) {
      validationErrors.push(
        "Please provide more detail in your message."
      );
    }

    setErrors(validationErrors);

    return validationErrors.length === 0;
  }

  async function handleSubmit() {
    setErrors([]);
    setSuccessMessage("");

    const isValid =
      validateForm();

    if (!isValid) {
      return;
    }

    try {
      // Future API integration

      console.log({
        name,
        email,
        company,
        subject,
        message,
      });

      setSuccessMessage(
        "Thank you for contacting AGPSIRA. Your inquiry has been prepared successfully."
      );

      setName("");
      setEmail("");
      setCompany("");
      setSubject("");
      setMessage("");
    } catch (error) {
      console.error(error);

      setErrors([
        "Unable to submit your inquiry at this time.",
      ]);
    }
  }

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      <section className="max-w-6xl mx-auto px-4 md:px-8 py-20 md:py-24">

        <h1 className="text-4xl md:text-6xl font-bold text-center mb-6">
          Contact Us
        </h1>

        <p className="text-center text-slate-300 text-lg md:text-xl max-w-4xl mx-auto mb-16 leading-8">
          We&apos;d love to hear from you.
          Whether you have questions about
          AGPSIRA Agreements, Share-In-Revenues (SiR),
          partnerships, support, or business opportunities,
          our team is available to help.
        </p>

        <div className="grid gap-8 md:grid-cols-2 mb-16">

          {contactSections.map((section) => (

            <div
              key={section.title}
              className="rounded-3xl border border-slate-800 bg-slate-900 p-8"
            >
              <h2 className="mb-4 text-2xl font-bold">
                {section.title}
              </h2>

              <p className="mb-6 leading-8 text-slate-300">
                {section.description}
              </p>

              <a
                href={`mailto:${section.email}`}
                className="text-blue-400 transition-colors hover:text-blue-300"
              >
                {section.email}
              </a>
            </div>

          ))}

        </div>

        <div className="rounded-3xl border border-slate-800 bg-slate-900 p-8">

          <h2 className="mb-4 text-3xl font-bold">
            Send Us A Message
          </h2>

          <p className="mb-8 text-slate-300">
            Use the form below to submit questions, partnership inquiries,
            support requests, or business opportunities.
          </p>

          {errors.length > 0 && (
            <div
              role="alert"
              className="mb-6 rounded-xl border border-red-900 bg-red-950/40 p-4 text-red-200"
            >
              <ul className="list-disc space-y-1 pl-5">
                {errors.map((error) => (
                  <li key={error}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {successMessage && (
            <p
              role="status"
              className="mb-6 rounded-xl border border-emerald-900 bg-emerald-950/40 p-4 text-emerald-200"
            >
              {successMessage}
            </p>
          )}

          <div className="space-y-6">
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Full Name"
              aria-label="Full Name"
              className="w-full rounded-2xl border border-slate-700 bg-slate-800 p-4 text-white placeholder:text-slate-500"
            />

            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Email Address"
              aria-label="Email Address"
              className="w-full rounded-2xl border border-slate-700 bg-slate-800 p-4 text-white placeholder:text-slate-500"
            />

            <input
              type="text"
              value={company}
              onChange={(event) => setCompany(event.target.value)}
              placeholder="Company (Optional)"
              aria-label="Company (Optional)"
              className="w-full rounded-2xl border border-slate-700 bg-slate-800 p-4 text-white placeholder:text-slate-500"
            />

            <input
              type="text"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              placeholder="Subject"
              aria-label="Subject"
              className="w-full rounded-2xl border border-slate-700 bg-slate-800 p-4 text-white placeholder:text-slate-500"
            />

            <textarea
              rows={8}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="How can we help you?"
              aria-label="How can we help you?"
              className="w-full rounded-2xl border border-slate-700 bg-slate-800 p-4 text-white placeholder:text-slate-500"
            />

            <button
              type="button"
              onClick={handleSubmit}
              className="w-full rounded-2xl bg-blue-600 py-4 font-semibold transition-colors hover:bg-blue-700"
            >
              Submit Inquiry
            </button>
          </div>

        </div>

      </section>

    </main>

  );

}